import React, { useCallback, useEffect, useRef, useState } from 'react';

import { DatePickerCalendar } from './date-picker-calendar';
import { formatDate, isValidDateInput } from './date-picker-utilities';
import { DatePickerProps } from './date-picker.types';
import { FormHelperText, Input, Popover } from '../..';

// eslint-disable-next-line max-lines-per-function
export const DatePicker = (props: DatePickerProps) => {
  const { onChange, selectedDate, onOpenChange, allowClear, errorProps, isDisabledDate, ...rest } = props;
  const ref = useRef<HTMLInputElement>();
  const [menuOpen, setMenuOpen] = useState(false);
  const [formattedDate, setFormattedDate] = useState<string>(formatDate(selectedDate));
  const size = rest.containerSize ?? 'large';

  useEffect(() => setFormattedDate(formatDate(selectedDate)), [selectedDate]);

  const onChangeCallback = useCallback(
    (selection: [Date?, Date?]) => {
      const formattedDate = formatDate(selection[0] as Date);
      onChange(selection[0] as Date);
      setFormattedDate(formattedDate);
      setMenuOpen(false);
      (ref.current as HTMLInputElement)?.blur();
    },
    [onChange]
  );

  const validate = useCallback(
    (input: string, isFinishedTyping: boolean) => {
      if (!isFinishedTyping) {
        // Note: If we call the onChangeCallback before the user is finished typing,
        //       the datepicker will close prematurely
        setFormattedDate(input);
        (ref.current as HTMLInputElement)?.focus();
        return;
      }

      const date = new Date(input);
      if (!isValidDateInput(input) || isDisabledDate?.(date)) {
        // The user has typed in an invalid date
        // So, clear the input
        onChangeCallback([undefined]);
        return;
      }

      // The user has typed in a date in the right format
      onChangeCallback([date]);
    },
    [isDisabledDate, onChangeCallback]
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => validate(e.target.value, false),
    [validate]
  );

  const setOpen = useCallback(
    (isOpen: boolean) => {
      setMenuOpen(isOpen);
      if (isOpen !== menuOpen) onOpenChange?.(isOpen);
      if (!isOpen) validate(formattedDate, true);
    },
    [formattedDate, menuOpen, onOpenChange, validate]
  );

  const onKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const isOpen = !menuOpen || e.key !== 'Enter';
      setOpen(isOpen);

      if (e.key === 'Enter') {
        // This is to handle the case where the user types in a date and
        // presses enter, but the date is in the format mm-dd-yy
        validate((e.target as HTMLInputElement).value, true);
      }
    },
    [menuOpen, setOpen, validate]
  );

  const date = formattedDate && isValidDateInput(formattedDate) ? new Date(formattedDate) : undefined;
  const selection = Number.isNaN(date?.getTime()) ? new Date() : date;

  return (
    <>
      <Popover
        open={menuOpen}
        trigger="external"
        innerContentClassName=""
        showArrow={false}
        onOpenChange={setOpen}
        autoPlacementOptions={{ autoAlignment: true, allowedPlacements: ['bottom', 'top'] }}
        content={
          <DatePickerCalendar
            {...rest}
            isDisabledDate={isDisabledDate}
            selectionStart={selection}
            onChange={onChangeCallback}
          />
        }
      >
        <Input
          ref={(thisInput) => (ref.current = thisInput as HTMLInputElement)}
          type="date"
          allowClear={allowClear}
          onKeyUp={onKeyUp}
          onFocus={() => setOpen(true)}
          value={formattedDate}
          onChange={onInputChange}
          containerSize={rest.containerSize}
          placeholder="mm/dd/yyyy"
        />
      </Popover>
      {errorProps && <FormHelperText {...{ ...errorProps, type: 'error', size: errorProps.size ?? size }} />}
    </>
  );
};

export default DatePicker;
